import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header/Header";
import CtaBand from "../../components/Home/CtaBand";
import ContactForm from "../../components/Forms/ContactForm";
import ChatWidget from "../../components/ChatWidget/ChatWidget";
import TitleBand from "../../components/TitleBand/TitleBand";
import SolutionExplainer from "./SolutionExplainer";
import { SOLUTION_GROUPS, findSolution, groupPath, itemPath, useSolutionTitles } from "./content";
import { useInView } from "../../util/useInView";
import Seo from "../../seo/Seo";
import "./solucao.css";

// /solucoes/:groupSlug — visão geral do tópico, os itens dele em cards e atalhos para os outros tópicos.
// A key remonta a página ao trocar de tópico, para as animações de entrada rodarem de novo.
export default function SolutionGroupPage() {
  const { groupSlug } = useParams();
  return <SolutionGroup key={groupSlug} groupSlug={groupSlug} />;
}

function SolutionGroup({ groupSlug }: { groupSlug?: string }) {
  const { t } = useTranslation();
  const { group } = findSolution(groupSlug);
  const { groupTitle, itemTitle } = useSolutionTitles();
  const [cardsRef, cardsState] = useInView<HTMLUListElement>(0.15);

  if (!group) return <Navigate to="/solucoes" replace />;

  const title = groupTitle(group);

  return (
    <div className="cz-page cz-scope cz-sol-page">
      <Seo title={title} description={group.tagline} path={groupPath(group)} />
      <Header />
      <main>
        <TitleBand
          crumbs={[{ label: t("header.solutions", "Soluções"), to: "/solucoes" }]}
          title={title}
          icon={group.icon}
        />

        <section className="cz-sol-section cz-sol-what" aria-labelledby="cz-sol-what-title">
          <div className="cz-sol-what__text">
            <span className="cz-eyebrow">{t("solutions_page.overview_eyebrow", "Visão geral")}</span>
            <h2 className="cz-sol-h2" id="cz-sol-what-title">
              {t("solutions_page.overview_title", { name: title, defaultValue: "O que é {{name}}" })}
            </h2>
            {group.description.map((paragraph) => (
              <p key={paragraph} className="cz-sol-lead">{paragraph}</p>
            ))}
          </div>
          <SolutionExplainer icon={group.icon} label={title} concepts={group.concepts} />
        </section>

        <section className="cz-sol-band" aria-labelledby="cz-sol-items-title">
          <div className="cz-sol-section">
            <div className="cz-sol-head">
              <span className="cz-eyebrow">{t("solutions_page.items_eyebrow", "Soluções")}</span>
              <h2 className="cz-sol-h2" id="cz-sol-items-title">
                {t("solutions_page.items_title", { name: title, defaultValue: "O que oferecemos em {{name}}" })}
              </h2>
            </div>
            <ul ref={cardsRef} className={`cz-sol-cards${cardsState}`}>
              {group.items.map((item, i) => {
                const Icon = item.icon;
                return (
                  <li key={item.key} style={{ "--i": i } as React.CSSProperties}>
                    <Link to={itemPath(group, item)} className="cz-sol-item-card">
                      <span className="cz-sol-item-card__icon" aria-hidden="true">
                        <Icon size={24} strokeWidth={1.7} />
                      </span>
                      <h3 className="cz-sol-item-card__title">{itemTitle(group, item)}</h3>
                      <p className="cz-sol-item-card__text">{item.tagline}</p>
                      <span className="cz-sol-item-card__more">
                        {t("solutions_page.learn_more", "Saiba mais")}
                        <ArrowRight size={16} aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <section className="cz-sol-section" aria-labelledby="cz-sol-others-title">
          <h2 className="cz-sol-card__title" id="cz-sol-others-title">
            {t("solutions_page.others_title", "Outras áreas")}
          </h2>
          <ul className="cz-sol-others">
            {SOLUTION_GROUPS.filter((other) => other.key !== group.key).map((other) => {
              const Icon = other.icon;
              return (
                <li key={other.key}>
                  <Link to={groupPath(other)} className="cz-sol-others__link">
                    <Icon size={18} aria-hidden="true" />
                    {groupTitle(other)}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

        <CtaBand />
        <ContactForm />
      </main>
      <ChatWidget />
    </div>
  );
}
